import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Select,
  Option,
  FormControl,
  FormLabel,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Sheet,
} from '@mui/joy';
import {
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
  MapTrifold as Map,
  DownloadSimple as Download,
  ArrowLeft,
  Calendar as CalendarMonth,
  Drop as InvertColors,
} from '@phosphor-icons/react';
import { canvasingApi } from '../../lib/canvasingApi';
import { format, parseISO, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface MarkerData {
  marker: google.maps.Marker;
  data: any;
}

export default function CanvasingMapReport() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    // Default to yesterday
    const yesterday = subDays(new Date(), 1);
    return format(yesterday, 'yyyy-MM-dd');
  });
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [mapInitAttempted, setMapInitAttempted] = useState(false);
  const [isGreyscale, setIsGreyscale] = useState(false);
  const markersRef = useRef<MarkerData[]>([]);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  // Fetch canvasing map data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['canvasing-map', selectedDate, selectedUserId],
    queryFn: async () => {
      console.log(`🗺️ Fetching canvasing map data for date: ${selectedDate}, userId: ${selectedUserId}`);
      try {
        const result = await canvasingApi.getCanvasingMapData({
          date: selectedDate,
          user_id: selectedUserId || undefined,
        });
        console.log(`🗺️ Successfully fetched canvasing data:`, result);
        return result;
      } catch (error) {
        console.error(`🗺️ Failed to fetch canvasing map data:`, error);
        throw error;
      }
    },
    retry: (failureCount, error) => {
      console.log(`🗺️ Query retry attempt ${failureCount} for error:`, error);
      // Retry up to 2 times for network errors, but not for auth errors
      if (failureCount < 2 && error instanceof Error && !error.message.includes('Authentication failed')) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });


  // Initialize Google Map when conditions are right
  const initializeMap = useCallback(async () => {
    console.log('🗺️ Attempting to initialize map...');
    console.log('🗺️ Container available:', !!mapContainerRef.current);
    
    // Log container dimensions and visibility
    if (mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      console.log('🗺️ Container dimensions:', {
        width: rect.width,
        height: rect.height,
        visible: rect.width > 0 && rect.height > 0,
        inViewport: rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth
      });
      console.log('🗺️ Container computed style:', {
        display: window.getComputedStyle(mapContainerRef.current).display,
        visibility: window.getComputedStyle(mapContainerRef.current).visibility,
        position: window.getComputedStyle(mapContainerRef.current).position
      });
    }
    
    console.log('🗺️ Google Maps ready:', !!(window.google && window.google.maps));
    console.log('🗺️ Map instance exists:', !!mapInstance);
    console.log('🗺️ Init attempted:', mapInitAttempted);

    if (mapInstance) {
      console.log('🗺️ Map instance already exists, skipping');
      return;
    }

    if (!mapContainerRef.current) {
      console.log('🗺️ Map container not available');
      return;
    }

    if (!window.google || !window.google.maps) {
      console.log('🗺️ Google Maps API not ready');
      return;
    }

    try {
      console.log('🗺️ Creating Google Map instance...');
      setMapInitAttempted(true);
      
      const map = new google.maps.Map(mapContainerRef.current, {
        center: { lat: 39.8283, lng: -98.5795 }, // Center of US
        zoom: 10,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
      });

      console.log('🗺️ Google Map instance created successfully:', map);
      setMapInstance(map);
      infoWindowRef.current = new google.maps.InfoWindow();
    } catch (error) {
      console.error('🗺️ Failed to initialize Google Map:', error);
      setMapInitAttempted(false); // Allow retry
    }
  }, [mapInstance, mapInitAttempted]);

  // Monitor for Google Maps API readiness
  useEffect(() => {
    console.log('🗺️ Google Maps readiness check effect');
    
    if (mapInstance) {
      console.log('🗺️ Map instance already exists, skipping readiness check');
      return;
    }

    let retryCount = 0;
    const maxRetries = 50; // 5 seconds of retries

    const checkAndInit = () => {
      console.log(`🗺️ Readiness check attempt ${retryCount + 1}/${maxRetries}`);
      
      if (window.google && window.google.maps && mapContainerRef.current && !mapInstance) {
        console.log('🗺️ All prerequisites ready, initializing map');
        initializeMap();
        return;
      }
      
      retryCount++;
      if (retryCount < maxRetries) {
        console.log('🗺️ Prerequisites not ready, retrying in 100ms', {
          googleMaps: !!(window.google && window.google.maps),
          container: !!mapContainerRef.current,
          mapInstance: !!mapInstance
        });
        setTimeout(checkAndInit, 100);
      } else {
        console.error('🗺️ Failed to initialize map after maximum retries');
        setMapInitAttempted(false); // Reset to allow manual retry
      }
    };

    // Start checking after a short delay
    const timeoutId = setTimeout(checkAndInit, 100);
    return () => clearTimeout(timeoutId);
  }, [initializeMap, mapInstance]);

  // Manual retry function
  const retryMapInitialization = () => {
    console.log('🗺️ Manual map initialization retry triggered');
    setMapInitAttempted(false);
    setMapInstance(null);
    setTimeout(() => initializeMap(), 100);
  };

  // Ensure map stays connected to container after data changes
  useEffect(() => {
    if (mapInstance && mapContainerRef.current) {
      console.log('🗺️ Triggering map resize after data change');
      // Force map to resize and redraw
      setTimeout(() => {
        if (mapInstance) {
          google.maps.event.trigger(mapInstance, 'resize');
          console.log('🗺️ Map resize triggered');
        }
      }, 100);
    }
  }, [data, mapInstance]);

  // Update markers when data changes
  useEffect(() => {
    console.log('🗺️ Marker update effect triggered. MapInstance:', !!mapInstance, 'Data:', !!data);
    
    if (!mapInstance || !data) {
      if (!mapInstance) console.log('🗺️ No map instance yet');
      if (!data) console.log('🗺️ No data yet');
      return;
    }

    console.log('🗺️ Processing data for markers. Knocks count:', data.knocks.length);

    // Clear existing markers
    markersRef.current.forEach(({ marker }) => marker.setMap(null));
    markersRef.current = [];

    if (data.knocks.length === 0) {
      console.log('🗺️ No knocks to display');
      // Force map resize even with no data
      setTimeout(() => {
        if (mapInstance) {
          google.maps.event.trigger(mapInstance, 'resize');
          console.log('🗺️ Map resize triggered (no data)');
        }
      }, 100);
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    // SVG circle marker factory — full color control, CSP-safe
    const makeIcon = (color: string) => ({
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22"><circle cx="11" cy="11" r="9" fill="${color}" stroke="white" stroke-width="2"/></svg>`
      )}`,
      scaledSize: new google.maps.Size(22, 22),
      anchor: new google.maps.Point(11, 11),
    });

    // Add markers for each knock
    data.knocks.forEach((knock) => {
      // Determine marker color by best outcome (highest priority first)
      let icon;
      if (knock.is_marketing_drop) {
        icon = makeIcon('#9C27B0'); // Purple — marketing drop
      } else if (knock.inspection_scheduled) {
        icon = makeIcon('#2E7D32'); // Medium green
      } else if (knock.inspection) {
        icon = makeIcon('#1B5E20'); // Dark green
      } else if (knock.left_info) {
        icon = makeIcon('#81C784'); // Light green
      } else if (knock.contact_info_collected) {
        icon = makeIcon('#0EA5E9'); // Sky — contact details captured
      } else if (knock.interested) {
        icon = makeIcon('#F97316'); // Orange — positive conversation
      } else if (knock.contact_made) {
        icon = makeIcon('#DC2626'); // Red — contact made, not interested
      } else {
        icon = makeIcon('#9E9E9E'); // Grey — no answer
      }

      // Skip knocks without valid coordinates
      if (!knock.lat || !knock.lng) {
        console.warn(`🗺️ Skipping knock ${knock.id} - missing coordinates`);
        return;
      }

      console.log(`🗺️ Creating marker for knock ${knock.id} at:`, { lat: knock.lat, lng: knock.lng });

      try {
        const marker = new google.maps.Marker({
          position: { lat: knock.lat, lng: knock.lng },
          map: mapInstance,
          title: `${knock.user} - ${format(parseISO(knock.timestamp), 'h:mm a')}`,
          icon,
        });

        console.log(`🗺️ Marker created successfully for knock ${knock.id}`);

        // Add click listener for info window
        marker.addListener('click', () => {
          const content = `
            <div style="padding: 8px; max-width: 250px; color: #222; font-family: sans-serif; font-size: 14px;">
              <h4 style="margin: 0 0 8px 0; color: #111;">${knock.user}</h4>
              <p style="margin: 4px 0; color: #333;"><strong>Time:</strong> ${format(
                parseISO(knock.timestamp),
                'h:mm a'
              )}</p>
              ${knock.address ? `<p style="margin: 4px 0; color: #333;"><strong>Address:</strong> ${knock.address}</p>` : ''}
              <div style="margin: 8px 0; display: flex; flex-direction: column; gap: 3px;">
                ${knock.contact_made ? '<span style="color: #1a7f37;">✓ Answer</span>' : '<span style="color: #cf2626;">✗ No Answer</span>'}
                ${knock.contact_info_collected ? '<span style="color: #0369a1;">📇 Contact captured</span>' : ''}
                ${knock.interested ? '<span style="color: #b45309;">★ Conversation</span>' : ''}
                ${knock.inspection ? '<span style="color: #7c3aed;">🔍 Inspection</span>' : ''}
                ${knock.inspection_scheduled ? '<span style="color: #0369a1;">📅 Inspection Scheduled</span>' : ''}
                ${knock.left_info ? '<span style="color: #0f766e;">📋 Left Info</span>' : ''}
                ${knock.is_marketing_drop ? '<span style="color: #7B1FA2;">📦 Marketing Drop</span>' : ''}
                ${knock.followup_required ? '<span style="color: #1d4ed8;">🚩 Follow-up Required</span>' : ''}
              </div>
              ${knock.notes ? `<p style="margin: 8px 0 0 0; font-style: italic; color: #555;">${knock.notes}</p>` : ''}
            </div>
          `;
          infoWindowRef.current?.setContent(content);
          infoWindowRef.current?.open(mapInstance, marker);
        });

        bounds.extend(marker.getPosition()!);
        markersRef.current.push({ marker, data: knock });
      } catch (error) {
        console.error(`🗺️ Error creating marker for knock ${knock.id}:`, error);
      }
    });

    console.log('🗺️ Created markers:', markersRef.current.length);

    if (markersRef.current.length > 0) {
      console.log('🗺️ Fitting map to bounds...');
      // Fit map to show all markers
      mapInstance.fitBounds(bounds);
      
      // Don't zoom in too far for single marker
      const listener = google.maps.event.addListener(mapInstance, 'idle', () => {
        const currentZoom = mapInstance.getZoom()!;
        console.log('🗺️ Map zoom level:', currentZoom);
        if (currentZoom > 16) {
          console.log('🗺️ Adjusting zoom to 16');
          mapInstance.setZoom(16);
        }
        google.maps.event.removeListener(listener);
      });
    } else {
      console.warn('🗺️ No markers created - map will stay at default location');
    }

    // Final validation that map is properly attached
    console.log('🗺️ Final validation - Map div:', mapInstance.getDiv());
    console.log('🗺️ Final validation - Container ref:', mapContainerRef.current);
    console.log('🗺️ Final validation - Containers match:', mapInstance.getDiv() === mapContainerRef.current);
  }, [mapInstance, data]);

  // Apply/remove greyscale style
  useEffect(() => {
    if (!mapInstance) return;
    mapInstance.setOptions({
      styles: isGreyscale ? [{ stylers: [{ saturation: -100 }] }] : [],
    });
  }, [isGreyscale, mapInstance]);

  // Navigate dates
  const handlePrevDate = () => {
    if (data?.prev_date) {
      setSelectedDate(data.prev_date);
    }
  };

  const handleNextDate = () => {
    if (data?.next_date) {
      setSelectedDate(data.next_date);
    }
  };

  // Export to CSV
  const handleExport = () => {
    if (!data) return;

    const csvContent = [
      ['User', 'Timestamp', 'Address', 'Contact Made', 'Interested', 'Contact captured', 'Follow-up Required', 'Marketing Drop', 'Notes', 'Latitude', 'Longitude'],
      ...data.knocks.map(knock => [
        knock.user,
        knock.timestamp,
        knock.address || '',
        knock.contact_made ? 'Yes' : 'No',
        knock.interested ? 'Yes' : 'No',
        knock.contact_info_collected ? 'Yes' : 'No',
        knock.followup_required ? 'Yes' : 'No',
        knock.is_marketing_drop ? 'Yes' : 'No',
        knock.notes || '',
        knock.lat,
        knock.lng,
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `canvasing-report-${selectedDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Back Button */}
      <Button
        variant="plain"
        color="neutral"
        startDecorator={<ArrowLeft />}
        onClick={() => navigate('/reports')}
        sx={{ mb: 3 }}
      >
        Back to Reports
      </Button>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        mb={3}
      >
        <Typography
          level="h2"
          startDecorator={<Map />}
          sx={{ fontSize: { xs: '1.25rem', md: '2rem' } }}
        >
          Canvasing Map Report
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant={isGreyscale ? 'solid' : 'outlined'}
            color="neutral"
            startDecorator={<InvertColors />}
            onClick={() => setIsGreyscale(g => !g)}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Greyscale
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Download />}
            onClick={handleExport}
            disabled={!data || data.knocks.length === 0}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {/* Date Navigation */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <IconButton
              variant="outlined"
              onClick={handlePrevDate}
              disabled={!data?.prev_date}
            >
              <ChevronLeft />
            </IconButton>

            <Stack alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, px: 1 }}>
              <Typography level="body-sm" startDecorator={<CalendarMonth />}>
                Report Date
              </Typography>
              <Typography
                level="h3"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                  textAlign: 'center',
                }}
              >
                {format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')}
              </Typography>
            </Stack>

            <IconButton
              variant="outlined"
              onClick={handleNextDate}
              disabled={!data?.next_date}
            >
              <ChevronRight />
            </IconButton>
          </Stack>

          {/* User Filter */}
          <FormControl sx={{ mt: 2 }}>
            <FormLabel>Filter by User</FormLabel>
            <Select
              value={selectedUserId}
              onChange={(_, value) => setSelectedUserId(value)}
              placeholder="All Users"
            >
              <Option value={null}>All Users</Option>
              {data?.stats.user_stats.map(user => (
                <Option key={user.user_id} value={user.user_id}>
                  {user.user_name} ({user.knock_count} knocks)
                </Option>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        {/* Map */}
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ height: 600, width: '100%', position: 'relative' }}>
              {/* Always render map container to prevent DOM replacement */}
              <Box
                ref={mapContainerRef}
                key="map-container" // Stable key to prevent re-mounting
                sx={{ 
                  height: '100%',
                  width: '100%',
                  borderRadius: 'sm',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: 'transparent'
                }}
              />
              
              {/* Overlay content based on state */}
              {isLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    backgroundColor: 'background.surface',
                    borderRadius: 'sm',
                    zIndex: 1000
                  }}
                >
                  <CircularProgress />
                  <Typography level="body-sm">Loading canvasing data...</Typography>
                </Box>
              )}

              {error && (
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex', 
                    flexDirection: 'column', 
                    p: 3, 
                    gap: 2,
                    backgroundColor: 'background.surface',
                    borderRadius: 'sm',
                    zIndex: 1000
                  }}
                >
                  <Alert color="danger" variant="soft">
                    <Typography level="title-sm">Failed to load canvasing data</Typography>
                    <Typography level="body-sm" sx={{ mt: 1 }}>
                      {error instanceof Error 
                        ? error.message 
                        : 'An unexpected error occurred while loading the map data.'}
                    </Typography>
                  </Alert>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => refetch()}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Retry Data
                    </Button>
                    {!mapInstance && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={retryMapInitialization}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Retry Map
                      </Button>
                    )}
                  </Stack>
                </Box>
              )}

              {!error && !isLoading && data?.knocks.length === 0 && (
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    zIndex: 500
                  }}
                >
                  <Alert color="neutral" variant="soft">
                    <Typography level="title-sm">No knocks recorded</Typography>
                    <Typography level="body-sm" sx={{ mt: 1 }}>
                      No canvasing activities were recorded for {format(parseISO(selectedDate), 'MMMM d, yyyy')}.
                    </Typography>
                  </Alert>
                </Box>
              )}

              {!error && !isLoading && data && data.knocks.length > 0 && !mapInstance && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    zIndex: 1000
                  }}
                >
                  <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                    <Typography level="title-sm">Map Loading Issue</Typography>
                    <Typography level="body-sm" sx={{ mt: 1 }}>
                      The Google Map failed to initialize properly.
                    </Typography>
                  </Alert>
                  <Button
                    variant="soft"
                    color="primary"
                    onClick={retryMapInitialization}
                  >
                    Initialize Map
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Statistics Sidebar */}
        <Stack spacing={3} sx={{ width: { xs: '100%', lg: 350 } }}>
          {/* Summary Stats — Funnel */}
          <Card>
            <CardContent>
              <Typography level="title-md" mb={2}>
                Summary Statistics
              </Typography>

              {/* Funnel: decreasing widths, centered */}
              <Stack spacing={0.5} alignItems="center">
                {/* Knocks — full width, grey */}
                <Sheet sx={{ p: 2, borderRadius: 'sm', width: '100%', backgroundColor: 'rgba(158,158,158,0.15)', textAlign: 'center' }}>
                  <Typography level="body-xs">Knocks</Typography>
                  <Typography level="h3">{data?.stats.total_knocks || 0}</Typography>
                </Sheet>

                {/* Answers — 85% width, yellow */}
                <Sheet sx={{ p: 1.5, borderRadius: 'sm', width: '85%', backgroundColor: 'rgba(234,179,8,0.18)', textAlign: 'center' }}>
                  <Typography level="body-xs">Answers</Typography>
                  <Typography level="h4">{data?.stats.contact_made || 0}</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{data?.stats.contact_rate || '0%'}</Typography>
                </Sheet>

                {/* Positive Conversations — 70% width, orange */}
                <Sheet sx={{ p: 1.5, borderRadius: 'sm', width: '70%', backgroundColor: 'rgba(249,115,22,0.15)', textAlign: 'center' }}>
                  <Typography level="body-xs">Conversations</Typography>
                  <Typography level="h4">{data?.stats.interested || 0}</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{data?.stats.interest_rate || '0%'}</Typography>
                </Sheet>

                {/* Contact captured — 62% width, sky */}
                <Sheet sx={{ p: 1.5, borderRadius: 'sm', width: '62%', backgroundColor: 'rgba(14,165,233,0.15)', textAlign: 'center' }}>
                  <Typography level="body-xs">Contact captured</Typography>
                  <Typography level="h4">{data?.stats.contact_info_collected ?? 0}</Typography>
                </Sheet>

                {/* Inspections — 50% width, dark green */}
                <Sheet sx={{ p: 1.5, borderRadius: 'sm', width: '50%', backgroundColor: 'rgba(27,94,32,0.18)', textAlign: 'center' }}>
                  <Typography level="body-xs">Inspections</Typography>
                  <Typography level="h4">{data?.stats.inspection || 0}</Typography>
                </Sheet>
              </Stack>

              {/* 3 boxes below funnel */}
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                {/* Insp. Scheduled — green */}
                <Sheet sx={{ p: 1.5, borderRadius: 'sm', flex: 1, backgroundColor: 'rgba(46,125,50,0.2)', textAlign: 'center' }}>
                  <Typography level="body-xs">Insp. Scheduled</Typography>
                  <Typography level="h4">{data?.stats.inspections_scheduled || 0}</Typography>
                </Sheet>

                {/* Left Info — light green */}
                <Sheet sx={{ p: 1.5, borderRadius: 'sm', flex: 1, backgroundColor: 'rgba(129,199,132,0.25)', textAlign: 'center' }}>
                  <Typography level="body-xs">Left Info</Typography>
                  <Typography level="h4">{data?.stats.left_info || 0}</Typography>
                </Sheet>

                {/* Marketing Drops — purple */}
                <Sheet sx={{ p: 1.5, borderRadius: 'sm', flex: 1, backgroundColor: 'rgba(123,31,162,0.15)', textAlign: 'center' }}>
                  <Typography level="body-xs" sx={{ color: '#7B1FA2' }}>Mkt. Drops</Typography>
                  <Typography level="h4" sx={{ color: '#7B1FA2' }}>{data?.stats.marketing_drops || 0}</Typography>
                </Sheet>
              </Stack>
            </CardContent>
          </Card>

          {/* User Performance */}
          <Card>
            <CardContent>
              <Typography level="title-md" mb={2}>
                Performance by User
              </Typography>
              
              <Stack spacing={2}>
                {data?.stats.user_stats.map(user => (
                  <Sheet key={user.user_id} variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                    <Typography level="body-md" fontWeight="lg">
                      {user.user_name}
                    </Typography>
                    <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                      <Chip size="sm" variant="soft">
                        {user.knock_count} knocks
                      </Chip>
                      <Chip size="sm" variant="soft" color="success">
                        {user.contacts_made} answers
                      </Chip>
                      {user.interested > 0 && (
                        <Chip size="sm" variant="soft" color="warning">
                          {user.interested} convos
                        </Chip>
                      )}
                      {(user.contact_info_collected ?? 0) > 0 && (
                        <Chip size="sm" variant="soft" color="primary">
                          {user.contact_info_collected} contacts
                        </Chip>
                      )}
                      {user.inspection > 0 && (
                        <Chip size="sm" variant="soft">
                          {user.inspection} insp.
                        </Chip>
                      )}
                      {user.inspections_scheduled > 0 && (
                        <Chip size="sm" variant="soft" color="primary">
                          {user.inspections_scheduled} scheduled
                        </Chip>
                      )}
                      {user.left_info > 0 && (
                        <Chip size="sm" variant="soft">
                          {user.left_info} left info
                        </Chip>
                      )}
                      {user.marketing_drops > 0 && (
                        <Chip size="sm" variant="soft" sx={{ backgroundColor: 'rgba(123,31,162,0.15)', color: '#7B1FA2' }}>
                          {user.marketing_drops} drops
                        </Chip>
                      )}
                    </Stack>
                  </Sheet>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* Legend */}
          <Card>
            <CardContent>
              <Typography level="title-md" mb={2}>
                Map Legend
              </Typography>

              <Stack spacing={1}>
                {[
                  { color: '#9C27B0', label: 'Marketing Drop' },
                  { color: '#2E7D32', label: 'Insp. Scheduled' },
                  { color: '#1B5E20', label: 'Inspection' },
                  { color: '#81C784', label: 'Left Info' },
                  { color: '#0EA5E9', label: 'Contact captured' },
                  { color: '#F97316', label: 'Conversation (positive)' },
                  { color: '#DC2626', label: 'Conversation (not positive)' },
                  { color: '#9E9E9E', label: 'No Answer' },
                ].map(({ color, label }) => (
                  <Stack key={label} direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 14, height: 14, bgcolor: color, borderRadius: '50%', border: '2px solid white', boxShadow: '0 0 0 1px #ccc', flexShrink: 0 }} />
                    <Typography level="body-sm">{label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Stack>
    </Box>
  );
}